<template>
  <div v-if="isMounted" class="fileInput">
    <Teleport :to="teleportTo || '#overlayportal'">
      <Transition name="swipe" appear>
        <div class="fileupload-overlay">
          <div v-if="uploadManager.files.value.length" class="fileupload-list">
            <FileUploadList
              :filecount="uploadManager.files.value.length"
              :validfile-count="uploadManager.validfilesCount.value"
              :max-files="fileOptions.maxFiles"
            >
              <FileUploadListItem
                v-for="file in uploadManager.files.value"
                :key="file.value.uuid"
                :file="file.value"
                :options="{
                  maxFilesize: fileOptions.maxFilesize,
                  acceptedFiles: fileOptions.acceptedFiles,
                }"
                @remove-file="handleRemoveFile(file.value.uuid)"
                @retry-file="handleRetryFileUpload(file.value.uuid)"
              />
            </FileUploadList>
          </div>
          <FileDropzone
            ref="dropzonepanel"
            :dropzone-enabled="dropZoneEnabled"
            :max-files="fileOptions.maxFiles"
            :max-filesize="fileOptions.maxFilesize"
            :accepted-files="fileOptions.acceptedFiles"
            :current-file-count="uploadManager.validfilesCount.value"
            @file-select="handleAddFiles"
          >
          </FileDropzone>
        </div>
      </Transition>
    </Teleport>
    <div class="actions">
      <div>
        <InputButton
          v-if="uploadStatus === 'pending' || uploadStatus === 'uploading'"
          variant="ghost"
          @click="cancel"
        >
          {{ t("fileupload.action.cancel") }}
        </InputButton>
        <InputButton
          v-else-if="uploadStatus === 'partialsuccess' || uploadStatus === 'failed'"
          variant="ghost"
          @click="finalizeProcess"
        >
          {{ t("fileupload.action.backtobot") }}
        </InputButton>
      </div>
      <div>
        <InputButton
          v-if="uploadStatus === 'pending'"
          variant="ghost"
          :disabled="tooManyFiles || uploadManager.validfilesCount.value === 0"
          @click="startUpload"
        >
          {{ t("fileupload.action.startupload") }}
          <IconUpload width="18px" height="18px" fill="black" />
        </InputButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { FileUploadOptions, Intent } from "@/types";
import appconfig from "@/appconfig";
import { useUploadManager } from "./useUploadManager";
import FileUploadListItem from "./FileUploadListItem.vue";
import FileDropzone from "./FileDropzone.vue";
import { useI18n } from "@/composables/useI18n";
import IconUpload from "@/svg/IconUpload.vue";
import InputButton from "@/components/common/InputButton.vue";
import FileUploadList from "./FileUploadList.vue";

const { t } = useI18n();
const props = defineProps<{
  jwtToken: string;
  botId: string;
  channelId: string;
  mockUploadFunction?: boolean;
  options: FileUploadOptions;
  teleportTo?: string;
}>();

const emit = defineEmits<{
  "on-complete": [intent: Intent];
  "on-cancel": [intent: Intent];
  "on-conversation-info": [text: string];
}>();

const defaultFileOptions: FileUploadOptions = {
  maxFiles: 1,
  maxFilesize: 50_000_000,
  acceptedFiles: [],
  tokens: [] as string[],
  uploadMultiple: true,
  generateTokens: false,
};
const fileOptions = computed(() => Object.assign({}, defaultFileOptions, props.options));
const tooManyFiles = computed(
  () => uploadManager.validfilesCount.value > fileOptions.value.maxFiles
);

// In case this a livechat file upload we generate tokens for each file
// and set the tokens to the fileOptions
if (fileOptions.value.generateTokens) {
  const livechatToken = Array.from({ length: fileOptions.value.maxFiles }, () => "livechat");
  fileOptions.value.tokens = livechatToken;
}

// Generate unique endpoint for file upload based on the bot, channel, jwtToken
// and optionally generateTokens flag if we are using LiveChat File Upload feature
const uniqueUserId = `${props.channelId}.widget.${props.jwtToken}`;
const queryParams = fileOptions.value.generateTokens
  ? `?generateToken=${fileOptions.value.generateTokens}`
  : "";
const apiEndpointUrl = `${appconfig.apiPath}/files/upload/${props.botId}/${uniqueUserId}${queryParams}`;

// create the upload manager thats hadnles all the upload logic and state
const uploadManager = useUploadManager(
  apiEndpointUrl,
  fileOptions.value.tokens,
  {
    maxFilesize: fileOptions.value.maxFilesize,
    acceptedFiles: fileOptions.value.acceptedFiles,
  },
  props.mockUploadFunction
);
const { status: uploadStatus, successfulUploadCount } = uploadManager;

const dropZoneEnabled = computed(
  () => uploadManager.files.value.length < fileOptions.value.maxFiles
);

const handleAddFiles = (files: File[]) => uploadManager.addFiles(files);
const handleRemoveFile = (id: string) => uploadManager.removeFile(id);
const handleRetryFileUpload = (id: string) => uploadManager.uploadSingleFile(id);

// we need this guard to prevent the teleport from rendering before the component is mounted
const isMounted = ref(false);
onMounted(() => (isMounted.value = true));
onUnmounted(() => (isMounted.value = false));

const finalizeProcess = async () => {
  const text =
    successfulUploadCount.value === 1
      ? t("fileupload.success.single")
      : t("fileupload.success.multiple", { count: successfulUploadCount.value });
  uploadManager.clearFiles();

  emit("on-conversation-info", text);
  emit("on-complete", {
    intent: {
      name: "upload_success",
      parameters: { confidence: 1 },
    },
  });
};

const startUpload = async () => uploadManager.uploadAllFiles();

const cancel = () => {
  uploadManager.clearFiles();
  emit("on-cancel", {
    text: "Cancel",
    intent: {
      name: "upload_cancel",
      parameters: { confidence: 1 },
    },
  });
};

watch(uploadStatus, (value) => {
  if (value === "success") {
    console.log("success");
    setTimeout(() => {
      finalizeProcess();
    }, 500);
  }
});
</script>

<style lang="scss" scoped>
.fileupload-wrapper {
  max-height: 100%;
  min-height: 100%;
  width: 100%;
  display: grid;
}

.fileupload-overlay {
  width: 100%;
  max-height: 100%;

  padding: 8px;
  padding-bottom: 80px;
  display: grid;
  grid-template-rows: auto 1fr;
  align-self: end;
  //grid-template-rows: auto 22px auto;

  background-color: var(--moin-inputs-background, white);
  border-radius: var(--kw-widget-global-borderradius-outer)
    var(--kw-widget-global-borderradius-outer) 0 0;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1);

  display: grid;
  /**
  TODO: grid + gap: 10px adds a gap at the bottom when there is nothing.
  Maybe just add margin/padding-top to the item that appears below the drop-area?
   */
  //gap: 10px;
}

.fileupload-list {
  overflow-y: auto;
  margin-bottom: 8px;
}

.actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 8px;
  border-radius: 0 0 var(--kw-widget-global-borderradius-outer)
    var(--kw-widget-global-borderradius-outer);
  background: white;
  color: #a0a0a0;
  width: 100%;

  > div {
    display: flex;
    align-items: center;

    /* TODO Can we remove this? It messes with the appearance of paddings */
    /* min-height: 50px; */
  }

  > :first-child {
    justify-content: start;
  }

  > :last-child {
    justify-content: end;
  }

  /* Add a fixed width */
}

.action_button {
  display: flex;
  outline: none;
  align-items: center;
  background: transparent;
  border: none;
  color: #3f3f3f;
  background: #f5f5f5;
  border-radius: 4px;

  &.passive {
    color: #a0a0a0;
  }

  font-size: 1.6rem;
  line-height: 1.9rem;
  padding: 10px 12px;
  @include default-font;
  @include default-focus;

  svg {
    margin-left: 10px;
    fill: #aaa;
  }

  &:disabled {
    color: #a0a0a0;
    cursor: not-allowed;
  }

  transition: all 0.5s ease;

  &:hover {
    cursor: pointer;
  }
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  height: 47px;
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0;
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.fade-leave-active {
  //position: absolute;
  position: relative;
}

.swipe-move,
.swipe-enter-active,
.swipe-leave-active {
  opacity: 1;
  transition: all 0.3s ease-out;
}

.swipe-enter-from,
.swipe-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.swipe-leave-active {
  //position: absolute;
  position: relative;
}
</style>
